import {
	Box,
	Button,
	createModal,
	createStyles,
	Divider,
	Flex,
	Icon,
	SegmentedControl,
	Stack,
	TFLoadingOverlay,
	TFText,
	Tooltip,
	UnstyledButton,
	useDisclosure,
} from '@tf/ui';
import { M, S } from '@tf/utils';
import type React from 'react';
import type { FC } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdditionalDocuments } from '@/components/payments/AdditionalDocuments';
import { EditInstructionModal } from '@/components/payments/EditInstructionModal';
import { SupportingDocuments } from '@/components/payments/SupportingDocuments';
import { useNestedPaymentForms } from '@/components/payments/useNestedPaymentForms';
import { orderTypesMap } from '@/components/utils';
import { useNodeIdByObjectUri } from '@/core/api/legacy';
import type { Form, InwardDetails, InwardMessage, NestedProfile, OutwardInstruction } from '@/core/types/payments';

const useStyles = createStyles(({ colors }) => ({
	chip: {
		fontSize: 14,
		height: 20,
		display: 'flex',
		alignItems: 'center',
		borderRadius: 12,
		padding: '0 8px',
	},
	link: {
		fontSize: 13,
		height: 32,
		display: 'flex',
		alignItems: 'center',
		color: colors.brand[6],
		'&:hover': {
			color: colors.brand[6],
			textDecoration: 'underline',
		},
	},
}));

interface Props {
	paymentObjectName: string;
	paymentId: string;
	paymentKind: 'inward' | 'outward';
	decisionWasMade: boolean;
}

export const PaymentDetails: FC<Props> = ({ paymentObjectName, paymentKind, decisionWasMade, paymentId }) => {
	const otherDetailsModal = useDisclosure();
	const editInstructionModal = useDisclosure();

	const { inwardDetails, outwardDetails, outwardForm, inwardForm, profile, isPending } =
		useNestedPaymentForms(paymentObjectName);

	const [instructionState, setInstructionState] = useState<'actual' | 'original'>(
		!inwardDetails ? 'original' : 'actual',
	);
	const isActual = 'actual' === instructionState;

	const authorisedPersons = outwardForm?.data?.authorised_persons;
	const supportingDocuments = outwardForm?.data?.attachments;

	const additionalDocuments = outwardDetails?.data.additional_documents || inwardDetails?.data.additional_documents;
	const detailsId = outwardDetails?.id || inwardDetails?.id;

	if (isPending || !profile) {
		return <TFLoadingOverlay />;
	}

	const preparedData = preparePaymentData({ inwardForm, inwardDetails, outwardForm, profile, isActual });
	const otherDetails = preparedData?.other;

	return (
		<Box p="20px 16px">
			{inwardForm && (
				<Flex mb="20px" justify="space-between" w="100%">
					<SegmentedControl
						disabled={!inwardDetails}
						value={instructionState}
						onChange={(value) => {
							setInstructionState(value as 'actual' | 'original');
						}}
						fullWidth
						data={['original', 'actual'].map((kind) => ({ value: kind, label: S.prettify(kind) }))}
					/>
					{!decisionWasMade && (
						<Button
							onClick={editInstructionModal.open}
							size="md"
							p="0 12px"
							leftSection={<Icon.IconPencil size={18} />}
							variant="light"
						>
							Edit
						</Button>
					)}
				</Flex>
			)}

			{Object.entries(preparedData ?? {}).map(([sectionKey, sectionData]) => {
				if (sectionKey === 'other') return;
				return (
					<Box key={sectionKey} mb="24px">
						<TFText fz="16px" lh="18px" fw="600" mb="20px">
							{getSectionName(paymentKind, sectionKey)}
						</TFText>
						<InstructionList data={sectionData} profileId={profile.id} />
					</Box>
				);
			})}
			{otherDetails && (
				<Button variant="light" mt="-14px" onClick={otherDetailsModal.open}>
					All details
				</Button>
			)}

			<SupportingDocuments supportingDocuments={supportingDocuments} />

			{Boolean(authorisedPersons?.length) && (
				<Box mt="32px">
					<TFText mb="12px" fz="16px" lh="18px" fw="600">
						Authorised Person&apos;s decision
					</TFText>
					{authorisedPersons?.map(({ name, email, signing_status }, index) => {
						return (
							<Stack gap="0" key={index}>
								<Flex mb="8px" align="center">
									<TFText mr="8px" lh="20px" fw="400" fz="14px">
										{name}
									</TFText>
									<SigningStatusChip status={signing_status} />
								</Flex>
								<TFText lh="18px" fw="500" fz="14px">
									{email}
								</TFText>
								<Divider m="12px 0" size="1px" color="#E5E7EB" />
							</Stack>
						);
					})}
				</Box>
			)}

			<AdditionalDocuments
				decisionWasMade={decisionWasMade}
				paymentId={paymentId}
				paymentKind={paymentKind}
				detailsId={detailsId}
				additionalDocuments={additionalDocuments}
			/>
			<OtherDetailsModal
				size="600"
				withinPortal
				opened={otherDetailsModal.isOpen}
				onClose={otherDetailsModal.close}
				title="Other details"
				contentProps={{ details: otherDetails, profileId: profile.id }}
			/>
			<EditInstructionModal
				title="Edit payment details"
				fullScreen
				opened={editInstructionModal.isOpen}
				onClose={editInstructionModal.close}
				contentProps={{
					paymentId,
					inwardDetails: inwardDetails?.data,
					id: inwardDetails?.id ?? '',
				}}
			/>
		</Box>
	);
};

const InstructionList: React.FC<{ data: Record<string, string>; profileId: string }> = ({ data, profileId }) => {
	const { data: profileNodeId } = useNodeIdByObjectUri(profileId);
	const { classes } = useStyles();
	const navigate = useNavigate();
	return (
		<>
			{Object.entries(data).map(([key, value], index, arr) => {
				const hasLink = key === LINK_KEY && profileNodeId;

				return (
					<Box key={key}>
						<Stack mt="16px" gap={2}>
							<Flex justify="space-between" align="start" w="100%">
								<TFText mb="8px" fz="14px" lh="normal" fw="400" c="#111827">
									{key}
								</TFText>
								{hasLink && (
									<Tooltip label="Go to all account payments" position="top">
										<Button
											variant="subtle"
											p="4px"
											color="gray"
											size="xs"
											leftSection={<Icon.IconCreditCardPay size={16} />}
											onClick={() => navigate(`/accounts/${profileNodeId}/payments`)}
										>
											Account payments
										</Button>
									</Tooltip>
								)}
							</Flex>
							{hasLink ? (
								<UnstyledButton onClick={() => navigate(`/accounts/${profileNodeId}`)} className={classes.link}>
									{value}
								</UnstyledButton>
							) : (
								<TFText fz="14px" lh="18px" mb="12px" c="#111827" fw="500">
									{value}
								</TFText>
							)}
						</Stack>
						{index !== arr.length - 1 && <Divider w="100%" size="1px" color="#E5E7EB" />}
					</Box>
				);
			})}
		</>
	);
};

interface ModalProps {
	details?: Record<string, string>;
	profileId?: string;
}

const OtherDetailsModal = createModal(({ details, profileId }: ModalProps) => {
	if (!details || !profileId) return null;
	return (
		<Box p="16px">
			<InstructionList data={details} profileId={profileId} />
		</Box>
	);
});

const SigningStatusChip: React.FC<{ status?: string }> = ({ status }) => {
	const { classes } = useStyles();
	const key = status?.toUpperCase() ?? '';

	const config: Record<string, { color: string; bg: string }> = {
		SIGNED: { color: 'green', bg: '#d3f9d8' },
		DECLINED: { color: 'red', bg: '#ffe3e3' },
		CANCELLED: { color: 'black', bg: '#d1d1d1' },
		DEFAULT: { color: 'grey', bg: '#f1f3f5' },
	};

	const { color, bg } = config[key] ?? config.DEFAULT;

	if (!status) return null;

	return (
		<Box className={classes.chip} style={{ color, background: bg }}>
			{S.prettify(status)}
		</Box>
	);
};

const LINK_KEY = 'Account name';

function preparePaymentData({
	inwardForm,
	inwardDetails,
	outwardForm,
	profile,
	isActual,
}: {
	inwardForm?: Form<InwardMessage>;
	inwardDetails?: Form<InwardDetails>;
	outwardForm?: Form<OutwardInstruction>;
	profile: NestedProfile;
	isActual: boolean;
}) {
	if (inwardForm) {
		const general = {
			'Order type': orderTypesMap[inwardForm.data.order_type] ?? '-',
			'Client ID': inwardForm?.data.client_id ?? '-',
			[LINK_KEY]: profile?.labels.name ?? '-',
			'Beneficiary customer': inwardForm?.data?.beneficiary_customer ?? '-',
			'Amount in USD equivalent': inwardForm.data.amount_usd ? M.format(Number(inwardForm.data.amount_usd)) : '-',
			'Value date/Currency/Interbank settled amount':
				inwardForm.data?.value_date_currency_interbank_settled_amount ?? '-',
			'Currency/Instructed amount': inwardForm.data?.currency_instructed_amount ?? '-',
		};
		const other = {
			'Time indication': inwardForm.data?.time_indication ?? '-',
			'Bank operation code': inwardForm.data?.bank_operation_code ?? '-',
			'Instruction code': inwardForm.data?.instruction_code ?? '-',
			'Transaction type code': inwardForm.data?.transaction_type_code ?? '-',
			'Exchange rate': inwardForm.data?.exchange_rate ?? '-',
			"Sender's correspondent": inwardForm.data?.sender_correspondent ?? '-',
			"Receiver's correspondent": inwardForm.data?.receiver_correspondent ?? '-',
			'Third reimbursement institution': inwardForm.data?.third_reimbursement_institution ?? '-',
			'Intermediary institution': inwardForm.data?.intermediary_institution ?? '-',
			'Account with institution': inwardForm.data?.account_with_institution ?? '-',
			'Remittance information': inwardForm.data?.remittance_information ?? '-',
			'Details of charges': inwardForm.data?.details_of_charges ?? '-',
			"Sender's charges": inwardForm.data?.sender_charges ?? '-',
			"Receiver's charges": inwardForm.data?.receiver_charges ?? '-',
			'Regulatory reporting': inwardForm.data?.regulatory_reporting ?? '-',
		};
		let details: Record<string, string>;
		if (inwardDetails && isActual) {
			details = {
				'Sender’s reference': inwardForm.data?.sender_reference ?? '-',
				'Sender jurisdiction': inwardDetails.data.parsed_data?.sender_jurisdiction ?? '-',
				'Sender name': inwardDetails.data.parsed_data?.sender_name ?? '-',
				'Sender IBAN': inwardDetails.data.parsed_data?.sender_iban ?? '-',
				'Sending institution': inwardForm.data?.sending_institution ?? '-',
				'Sender bank jurisdiction': inwardDetails.data.parsed_data?.sender_bank_jurisdiction ?? '-',
				'Sender bank name': inwardDetails.data.parsed_data?.sender_bank_name ?? '-',
				'Sender to receiver information': inwardDetails.data?.purpose_of_payment ?? '-',
			};
		} else {
			details = {
				'Sender’s reference': inwardForm.data?.sender_reference ?? '-',
				'Ordering customer': inwardForm.data?.ordering_customer ?? '-',
				'Sending institution': inwardForm.data?.sending_institution ?? '-',
				'Ordering institution': inwardForm.data?.ordering_institution ?? '-',
				'Sender to receiver information': inwardForm.data?.sender_to_receiver_information ?? '-',
			};
		}
		return { general, details, other };
	}
	if (outwardForm) {
		return {
			general: {
				'Order type': orderTypesMap[outwardForm.data.order_type] ?? '-',
				'Client ID': outwardForm?.data.client_id ?? '-',
				[LINK_KEY]: profile?.labels.name ?? '-',
				'Sender account number': outwardForm.data.sender_account_number ?? '-',
				'Amount in USD equivalent': outwardForm.data.amount_usd ? M.format(Number(outwardForm.data.amount_usd)) : '-',
				Amount: outwardForm.data.amount ? M.format(Number(outwardForm.data.amount)) : '-',
				Currency: outwardForm.data.amount_currency ?? '-',
			},
			details: {
				'Recipient name': outwardForm.data.recipient_name ?? '-',
				'Recipient address': outwardForm.data.recipient_address ?? '-',
				'Recipient jurisdiction': outwardForm.data.recipient_jurisdiction ?? '-',
				'Recipient account number': outwardForm.data.recipient_account_number ?? '-',
				'Recipient IBAN': outwardForm.data.recipient_iban ?? '-',
				'Bank name': outwardForm.data.recipient_bank_name ?? '-',
				'Recipient bank jurisdiction': outwardForm.data.recipient_bank_jurisdiction ?? '-',
				'Payment details': outwardForm.data.payment_details ?? '-',
			},
			other: {
				'Charges details': outwardForm.data.charges_details ?? '-',
				'Charges/fees amount': outwardForm.data.charges_fees_amount
					? M.format(Number(outwardForm.data.charges_fees_amount))
					: '-',
				'Charges/fees currency': outwardForm.data.charges_fees_currency ?? '-',
				'Payment date': outwardForm.data.payment_date ?? '-',
				'Recipient bank SWIFT BIC code': outwardForm.data.recipient_bank_swift_bic_code ?? '-',
				'Routing code (ABA/FEDWIRE)': outwardForm.data.routing_code ?? '-',
				'Sort code': outwardForm.data.sort_code ?? '-',
				'Intermediary bank SWIFT/BIC code': outwardForm.data.intermediary_bank_swift_bic_code ?? '-',
				'Intermediary bank account number': outwardForm.data.intermediary_bank_account_number ?? '-',
				'Payment details': outwardForm.data.purpose_of_payment ?? '-',
				'Recipient account holder nature of business/occupation':
					outwardForm.data.recipient_account_holder_nature_of_business_occupation ?? '-',
				'Current end client bank account velocity': outwardForm.data.current_end_client_bank_account_velocity ?? '-',
			},
		};
	}
}

function getSectionName(paymentKind: 'inward' | 'outward', sectionName: string) {
	if (sectionName === 'general') {
		return 'General information';
	}
	if (sectionName === 'details' && paymentKind === 'inward') {
		return 'Sender details';
	}
	if (sectionName === 'details' && paymentKind === 'outward') {
		return 'Recipient details';
	}
	return 'Other details';
}
